import { Box, Typography } from '@mui/material';
import Papa from 'papaparse';
import { useState } from 'react';
import {
  MAX_VESSEL_BATCH_SIZE,
  MAX_VESSEL_EMAIL_RECIPIENTS_LIMIT,
  MyFleetCsvHeaders,
} from '../../../../models/my-fleet.model';
import { isValidEmail } from '../../my-fleet-vessel-item/pro-alert/pro-alert-helpers';
import CsvUploadStatus from './my-fleet-csv-upload-status';
import CsvCreateVesselsButtonGroup from './upload-button-group';

function parseAndValidateCsvResults(
  results: any,
  setFailedEmails: (emails: string[]) => void,
  setFailedImos: (imos: string[]) => void,
  setVessels: (vessels: { imo: number; emails: string[] }[]) => void,
  setUploadError?: (msg: string | null) => void
) {
  const fileVessels: { imo: number; emails: string[] }[] = [];
  const emailsFailedValidation: string[] = [];
  const imosFailedValidation: string[] = [];
  const imoList: string[] = [];

  const rowsExceedingEmailLimit: number[] = [];
  results.data.forEach((row: any, idx: number) => {
    const imoRaw = row[MyFleetCsvHeaders.IMO];
    const vesselEmailsRaw = row[MyFleetCsvHeaders.VesselContactEmails] || '';
    const shoreEmailsRaw = row[MyFleetCsvHeaders.ShoreContactEmails] || '';

    // skip rows that do not have any data
    if (
      (!imoRaw || String(imoRaw) === '') &&
      vesselEmailsRaw === '' &&
      shoreEmailsRaw === ''
    ) {
      return;
    }

    // Validate IMO
    if (!imoRaw || !/^\d{7}$/.test(imoRaw)) {
      imosFailedValidation.push(imoRaw || '');
      return;
    }

    imoList.push(imoRaw);

    // Collect and validate emails
    const allEmails = [
      ...vesselEmailsRaw.split(',').map((email: string) => email.trim()),
      ...shoreEmailsRaw.split(',').map((email: string) => email.trim()),
    ];

    // Enforce max 20 emails per row
    if (
      allEmails.filter((email) => email).length >
      MAX_VESSEL_EMAIL_RECIPIENTS_LIMIT
    ) {
      rowsExceedingEmailLimit.push(idx + 2); // +2 for header and 0-index
      return;
    }

    // Separate valid and invalid emails
    const validEmails: string[] = [];
    allEmails.forEach((email) => {
      if (email) {
        if (isValidEmail(email)) {
          if (!validEmails.includes(email)) validEmails.push(email);
        } else if (!emailsFailedValidation.includes(email))
          emailsFailedValidation.push(email);
      }
    });

    fileVessels.push({
      imo: Number(imoRaw),
      emails: validEmails,
    });
  });

  // If any rows exceeded the email limit, show error and clear state
  if (rowsExceedingEmailLimit.length > 0 && setUploadError) {
    if (rowsExceedingEmailLimit.length === 1) {
      setUploadError(
        `Vessel on row ${rowsExceedingEmailLimit[0]} exceeds the maximum of ${MAX_VESSEL_EMAIL_RECIPIENTS_LIMIT} emails. Please reduce the number of emails to ${MAX_VESSEL_EMAIL_RECIPIENTS_LIMIT} or less.`
      );
    } else {
      setUploadError(
        `Multiple vessels contain more than ${MAX_VESSEL_EMAIL_RECIPIENTS_LIMIT} emails. Please reduce the number of emails in each row to ${MAX_VESSEL_EMAIL_RECIPIENTS_LIMIT} or less.`
      );
    }
    setFailedEmails([]);
    setFailedImos([]);
    setVessels([]);
    return;
  }

  // Check for duplicate IMOs
  const imoCounts: Record<string, number> = {};
  imoList.forEach((imo) => {
    imoCounts[imo] = (imoCounts[imo] || 0) + 1;
  });
  const duplicateImos = Object.keys(imoCounts).filter(
    (imo) => imoCounts[imo] > 1
  );
  if (duplicateImos.length > 0 && setUploadError) {
    setUploadError(
      `Duplicate IMOs found in file: ${duplicateImos.join(
        ', '
      )}. Please remove duplicates before uploading.`
    );
    setFailedEmails([]);
    setFailedImos([]);
    setVessels([]);
    return;
  }

  setFailedEmails(emailsFailedValidation);
  setFailedImos(imosFailedValidation);
  setVessels(fileVessels);
}

interface MyFleetCsvUploadButtonProps {
  onBatchSubmit: (vessels: { imo: number; emails: string[] }[]) => void;
}

export default function MyFleetCsvUploadButton({
  onBatchSubmit,
}: MyFleetCsvUploadButtonProps) {
  const [file, setFile] = useState<File | null>(null);
  const [clearRef, setClearRef] = useState(false);
  const [failedEmails, setFailedEmails] = useState<string[]>([]);
  const [failedImos, setFailedImos] = useState<string[]>([]);
  const [vessels, setVessels] = useState<{ imo: number; emails: string[] }[]>(
    []
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Please select a CSV file.');
      setFailedEmails([]);
      setFailedImos([]);
      setVessels([]);
      return;
    }

    setIsParsing(true);
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      transformHeader: (header) => header.trim().toLowerCase(),
      transform: (value) => (typeof value === 'string' ? value.trim() : value),
      complete: (results) => {
        // Only clear error states after parsing
        setFailedEmails([]);
        setFailedImos([]);
        setVessels([]);
        setUploadError(null);
        parseAndValidateCsvResults(
          results,
          setFailedEmails,
          setFailedImos,
          setVessels,
          setUploadError
        );
        setIsParsing(false);
      },
      error: () => {
        setUploadError('Error parsing CSV file. Please check the file format.');
        setFailedEmails([]);
        setFailedImos([]);
        setVessels([]);
        setIsParsing(false);
      },
    });
  };

  const handleClear = () => {
    setFile(null);
    setFailedEmails([]);
    setFailedImos([]);
    setVessels([]);
    setClearRef((prev) => !prev);
  };

  const handleSubmit = () => {
    if (vessels.length > 0) {
      onBatchSubmit(vessels);
    }
  };

  const getNumberofAlerts = () =>
    vessels.filter((v) => v.emails.length > 0).length;

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography sx={{ textAlign: 'left' }}>
        {file ? `Selected file: ${file.name}` : 'No file selected'}
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: '48%',
        }}
      >
        <CsvCreateVesselsButtonGroup
          onSubmit={handleSubmit}
          onCancel={handleClear}
          onChange={handleFileChange}
          clearRef={clearRef}
          submitDisabled={
            !file ||
            failedEmails.length > 0 ||
            failedImos.length > 0 ||
            vessels.length === 0 ||
            vessels.length > MAX_VESSEL_BATCH_SIZE ||
            uploadError !== null
          }
          cancelDisabled={!file}
        />
      </Box>
      {!isParsing && (
        <CsvUploadStatus
          file={file}
          failedEmails={failedEmails}
          failedImos={failedImos}
          vessels={vessels}
          uploadError={uploadError}
          numAlerts={getNumberofAlerts()}
        />
      )}
    </Box>
  );
}
